import React from 'react';
import '../../styles/components/AsperaUserMenu.scss';
import { Switcher, SwitcherItem, Theme } from '../Carbon';
import { language } from '../../services/LanguageService';
import { withRouter } from 'react-router-dom';
import { getEnvironmentVar } from '../../helpers/helpers';
import { logout, openNewTab } from '../../helpers/starterHelpers';
import { SubManager } from '../../classes/SubManager';
import { openAppInfoModal } from '../../helpers/components';
import { AsperaImageRender } from '../AsperaImageRender';
import { modal } from '../../services/ModalService';
import { ModalChangeTheme } from '../ModalChangeTheme';
import { tracking } from '../../services/TrackingService';
/**
 * React Component
 *
 * The AsperaUserMenu component renders the user menu (right menu dropdown)
 *
 * Offers the following features:
 * Logout (via envVariable `logout_url`) - And will clear storage regardless of logout URL
 * Third party Logout (via envVariable `third_logout_url`)
 * Settings page via `settingsOnClick`
 *
 * @example
 * ```javascript
 * <AsperaUserMenu />
 * ```
 */
export const AsperaUserMenu = withRouter(class AsperaUserMenu extends React.Component {
    constructor() {
        super(...arguments);
        this.subManager = new SubManager();
        this.openUserSettings = () => {
            const { settingsOnClick, onClick } = this.props;
            if (typeof settingsOnClick === 'function')
                settingsOnClick();
            onClick();
        };
        this.thirdPartyLogout = () => {
            if ((getEnvironmentVar('third_logout_url'))) {
                openNewTab(getEnvironmentVar('third_logout_url'), true);
            }
            this.logout();
        };
        this.logout = () => {
            const { onClick, preLogoutActions } = this.props;
            logout(this.subManager, onClick, preLogoutActions);
        };
        this.login = () => {
            const { onLoginClick, onClick } = this.props;
            onClick();
            if (typeof onLoginClick === 'function')
                onLoginClick();
        };
        this.openAppInfo = () => {
            const { onClick } = this.props;
            onClick();
            openAppInfoModal(this.subManager);
        };
        this.openCookiePreferences = () => {
            const { onClick } = this.props;
            const element = document.querySelector('#teconsent a') || document.querySelector('#teconsent button');
            onClick();
            if (element)
                element.click();
        };
        this.changeTheme = () => {
            const { onClick } = this.props;
            const modalInfo = modal.prepare();
            const modalProps = {
                modalHeading: language.get('header.changeTheme'),
                passiveModal: true,
                size: 'sm',
            };
            this.subManager.setSubscription(modal.create(modalInfo.id, modalProps, React.createElement(ModalChangeTheme, { modalClose: modalInfo.callback })).subscribe({
                next: () => {
                    onClick();
                },
                error: () => {
                    onClick();
                },
            }));
        };
    }
    get cookieText() {
        const element = document.querySelector('#teconsent a') || document.querySelector('#teconsent button');
        if (element)
            return element.innerHTML;
        return undefined;
    }
    get userOptions() {
        const { user, settingsOnClick, altFunctionality, onLoginClick } = this.props;
        const cookieText = this.cookieText;
        return [
            {
                name: language.get('userMenu.accountSettings'),
                onClick: this.openUserSettings,
                show: !!(user.id && !user.publicUser && settingsOnClick),
            },
            {
                name: language.get('header.changeTheme'),
                onClick: () => {
                    this.changeTheme();
                },
                show: !!(user.id && !user.publicUser && tracking.allowLightDarkModes),
            },
            {
                name: cookieText,
                onClick: this.openCookiePreferences,
                show: !!cookieText,
            },
            {
                name: language.get('userMenu.appInfo'),
                onClick: this.openAppInfo,
                show: !!altFunctionality,
            },
            {
                name: getEnvironmentVar('third_logout_name') || language.get('userMenu.logoutThirdParty'),
                onClick: this.thirdPartyLogout,
                show: (!user.publicUser && !!getEnvironmentVar('third_logout_url')),
            },
            {
                name: language.get('userMenu.logout'),
                onClick: this.logout,
                show: !user.publicUser,
            },
            {
                name: language.get('userMenu.login'),
                onClick: this.login,
                show: !!(user.publicUser && onLoginClick),
            },
        ];
    }
    /** 21 possible colors */
    getImageNumber(name) {
        let setting = name.length;
        if (setting > 21) {
            setting = 21;
        }
        return setting;
    }
    getUserImage() {
        const { user } = this.props;
        if (user.avatar_image_url) {
            const imageProp = {
                backgroundImage: `url(${user.avatar_image_url})`,
            };
            return React.createElement("div", { className: "image", style: imageProp });
        }
        else {
            const name = user.displayName || 'A';
            return React.createElement("div", { className: `image image-backup image-backup-background-${this.getImageNumber(name)}` },
                React.createElement(AsperaImageRender, { altText: name }));
        }
    }
    componentWillUnmount() {
        this.subManager.unsubscribeAll();
    }
    render() {
        const { className, user } = this.props;
        return (React.createElement(Theme, { theme: "g100", className: `aspera-user-menu ${className ? className : ''}`, "data-auto": "user-menu-wrapper" },
            React.createElement("div", { className: "user-information" },
                React.createElement("div", { className: "user-information-image" }, this.getUserImage()),
                React.createElement("div", { className: "user-information-text" },
                    React.createElement("div", { className: "primary-text" }, user.displayName),
                    React.createElement("div", { className: "secondary-text", title: user.email }, user.email),
                    React.createElement("div", { className: "secondary-text", title: user.displayUserRole }, user.displayUserRole))),
            React.createElement(Switcher, { "aria-label": user.displayName }, this.userOptions.filter(option => option.show).map((option, index) => {
                return React.createElement(SwitcherItem, { key: index, "aria-label": option.name, onClick: option.onClick, "data-auto": `user-menu-item-${option.name}` }, option.name);
            }))));
    }
});
